import React, { useState, useEffect } from "react";
import {
  FaTimes,
  FaBolt,
  FaCheck,
  FaExclamationTriangle,
  FaMinus,
  FaPlus,
  FaSitemap,
} from "react-icons/fa";
import { formatDisplayName } from "../../../utils/textUtils";
import { useUpdateSiteCriticalValue } from "../../../custome_hooks/sites/useUpdateSite";

interface SiteData {
  id: string;
  name: string;
  description?: string;
  siteExternalId?: number | string;
  criticalResistanceValue?: number;
  [key: string]: any;
}

interface SetCriticalValueModalProps {
  isOpen: boolean;
  onClose: () => void;
  site: SiteData | null;
  onSuccess?: () => void;
}

const PRESET_VALUES = [1.0, 1.5, 1.6, 2.0, 3.0, 5.0];

const SetCriticalValueModal: React.FC<SetCriticalValueModalProps> = ({
  isOpen,
  onClose,
  site,
  onSuccess,
}) => {
  const [criticalValue, setCriticalValue] = useState<number>(3.0);
  const [inputValue, setInputValue] = useState<string>("3.0");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { mutate: updateCriticalValue, isPending } = useUpdateSiteCriticalValue();

  useEffect(() => {
    if (site) {
      const initial =
        site.criticalResistanceValue != null &&
        !isNaN(Number(site.criticalResistanceValue))
          ? Number(site.criticalResistanceValue)
          : 3.0;
      setCriticalValue(initial);
      setInputValue(initial.toString());
      setErrorMsg(null);
    }
  }, [site, isOpen]);

  if (!isOpen || !site) return null;

  const handlePresetClick = (val: number) => {
    setCriticalValue(val);
    setInputValue(val.toString());
    setErrorMsg(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setInputValue(raw);
    const num = parseFloat(raw);
    if (!isNaN(num) && num > 0 && num < 100) {
      setCriticalValue(Math.round(num * 100) / 100);
      setErrorMsg(null);
    } else if (raw === "" || isNaN(num) || num <= 0) {
      setErrorMsg("Please enter a valid positive resistance threshold (e.g., 1.6)");
    }
  };

  const adjustValue = (delta: number) => {
    const next = Math.max(
      0.1,
      Math.min(50.0, Math.round((criticalValue + delta) * 10) / 10)
    );
    setCriticalValue(next);
    setInputValue(next.toString());
    setErrorMsg(null);
  };

  const handleSave = () => {
    const num = parseFloat(inputValue);
    if (isNaN(num) || num <= 0) {
      setErrorMsg("Threshold must be a valid positive number.");
      return;
    }

    updateCriticalValue(
      {
        id: site.id,
        name: site.name,
        criticalResistanceValue: num,
      },
      {
        onSuccess: () => {
          if (onSuccess) onSuccess();
          onClose();
        },
        onError: (err: any) => {
          const message =
            err?.response?.data?.message ||
            err?.message ||
            "Failed to update critical value. Please check connection and try again.";
          setErrorMsg(message);
        },
      }
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-5 md:p-6 animate-in fade-in duration-200">
      {/* Dark Ambient Backdrop with blur */}
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-md transition-opacity"
        onClick={isPending ? undefined : onClose}
      />

      {/* Modal Dialog Card */}
      <div
        className="relative w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl transition-all duration-300 transform scale-100 flex flex-col max-h-[92vh]"
        style={{
          background:
            "linear-gradient(165deg, rgba(17, 24, 45, 0.98) 0%, rgba(9, 14, 28, 0.99) 100%)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow:
            "0 25px 60px -15px rgba(0, 0, 0, 0.85), 0 0 40px rgba(59, 130, 246, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.12)",
        }}
      >
        {/* Ambient decorative glow circles */}
        <div
          className="absolute -top-24 -right-24 w-60 h-60 rounded-full pointer-events-none opacity-20 blur-3xl"
          style={{
            background: "radial-gradient(circle, #3b82f6 0%, transparent 70%)",
          }}
        />
        <div
          className="absolute -bottom-24 -left-24 w-60 h-60 rounded-full pointer-events-none opacity-15 blur-3xl"
          style={{
            background: "radial-gradient(circle, #f59e0b 0%, transparent 70%)",
          }}
        />

        {/* Modal Header */}
        <div
          className="flex items-center justify-between px-5 sm:px-6 py-4 sm:py-5 relative z-10 flex-shrink-0"
          style={{ borderBottom: "1px solid rgba(255, 255, 255, 0.07)" }}
        >
          <div className="flex items-center gap-3">
            <div
              className="p-2 sm:p-2.5 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0"
              style={{
                background:
                  "linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(234, 88, 12, 0.1) 100%)",
                border: "1px solid rgba(245, 158, 11, 0.3)",
                boxShadow: "0 0 16px rgba(245, 158, 11, 0.2)",
              }}
            >
              <FaBolt className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-white tracking-wide">
                Set Critical Value
              </h2>
              <p className="text-[11px] sm:text-xs text-slate-400">
                Configure resistance alert threshold
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            disabled={isPending}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30 cursor-pointer"
            title="Close"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 relative z-10 custom-scrollbar overflow-y-auto flex-1 min-h-0">
          {/* Target Site Info Badge */}
          <div
            className="flex items-center justify-between p-3 sm:p-3.5 rounded-2xl gap-3"
            style={{
              background: "rgba(255, 255, 255, 0.03)",
              border: "1px solid rgba(255, 255, 255, 0.06)",
            }}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 rounded-xl bg-blue-500/10 text-blue-400 border border-blue-500/20 flex-shrink-0">
                <FaSitemap className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  Target Site
                </div>
                <div className="text-xs sm:text-sm font-bold text-white truncate">
                  {formatDisplayName(site.name)}
                </div>
              </div>
            </div>
            {site.siteExternalId && (
              <span className="text-[11px] sm:text-xs px-2.5 py-1 rounded-full bg-slate-800 text-slate-300 border border-slate-700 font-mono flex-shrink-0">
                EXT: #{site.siteExternalId}
              </span>
            )}
          </div>

          {/* Interactive Numeric Control Card */}
          <div
            className="p-4 sm:p-5 rounded-2xl space-y-3.5 sm:space-y-4"
            style={{
              background:
                "linear-gradient(180deg, rgba(26, 36, 64, 0.45) 0%, rgba(15, 22, 40, 0.65) 100%)",
              border: "1px solid rgba(59, 130, 246, 0.2)",
              boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.05)",
            }}
          >
            <div className="flex items-center justify-between flex-wrap gap-1">
              <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-slate-300">
                Critical Threshold (Ω)
              </span>
              <span className="text-[11px] text-amber-400 font-medium">
                Standard: 1.6 Ω – 3.0 Ω
              </span>
            </div>

            {/* Stepper + Input */}
            <div className="flex items-center gap-2 sm:gap-3 justify-center">
              <button
                type="button"
                onClick={() => adjustValue(-0.1)}
                disabled={isPending || criticalValue <= 0.2}
                className="w-10 h-10 sm:w-11 sm:h-11 rounded-2xl flex items-center justify-center bg-white/[0.05] hover:bg-white/[0.12] active:scale-95 text-slate-300 hover:text-white border border-white/10 transition-all cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
              >
                <FaMinus className="w-3.5 h-3.5" />
              </button>

              <div className="relative flex-1 max-w-[170px] sm:max-w-[200px]">
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="50"
                  value={inputValue}
                  onChange={handleInputChange}
                  disabled={isPending}
                  className="w-full text-center text-2xl sm:text-3xl font-extrabold text-white bg-black/40 border border-white/15 focus:border-blue-500 rounded-2xl py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all font-mono tracking-tight"
                  placeholder="1.6"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs sm:text-sm font-semibold text-slate-400 pointer-events-none">
                  Ω
                </span>
              </div>

              <button
                type="button"
                onClick={() => adjustValue(0.1)}
                disabled={isPending || criticalValue >= 50}
                className="w-10 h-10 sm:w-11 sm:h-11 rounded-2xl flex items-center justify-center bg-white/[0.05] hover:bg-white/[0.12] active:scale-95 text-slate-300 hover:text-white border border-white/10 transition-all cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
              >
                <FaPlus className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Range Slider */}
            <div className="pt-2 px-1">
              <input
                type="range"
                min="0.5"
                max="10.0"
                step="0.1"
                value={Math.min(10.0, Math.max(0.5, criticalValue))}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  setCriticalValue(val);
                  setInputValue(val.toString());
                  setErrorMsg(null);
                }}
                disabled={isPending}
                className="w-full h-1.5 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
              />
              <div className="flex justify-between text-[10px] font-mono text-slate-500 mt-1">
                <span>0.5 Ω</span>
                <span>2.5 Ω</span>
                <span>5.0 Ω</span>
                <span>7.5 Ω</span>
                <span>10.0 Ω</span>
              </div>
            </div>

            {/* Quick Presets Pills */}
            <div className="space-y-1.5 pt-1">
              <div className="text-[10px] sm:text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                Quick Presets:
              </div>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {PRESET_VALUES.map((val) => {
                  const isSelected = Math.abs(criticalValue - val) < 0.01;
                  return (
                    <button
                      key={val}
                      type="button"
                      onClick={() => handlePresetClick(val)}
                      disabled={isPending}
                      className={`px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-xl text-xs font-semibold font-mono transition-all duration-200 cursor-pointer ${
                        isSelected
                          ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-[0_0_12px_rgba(59,130,246,0.5)] border border-blue-400/50 scale-105"
                          : "bg-white/[0.04] text-slate-400 hover:text-slate-200 hover:bg-white/[0.08] border border-white/[0.06]"
                      }`}
                    >
                      {val.toFixed(1)} Ω
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Live Dynamic Threshold Preview */}
          <div
            className="p-3.5 sm:p-4 rounded-2xl space-y-2.5"
            style={{
              background: "rgba(0, 0, 0, 0.25)",
              border: "1px solid rgba(255, 255, 255, 0.05)",
            }}
          >
            <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
              <span>Threshold Evaluation Preview</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <div className="p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 flex items-center gap-2">
                <FaCheck className="w-3.5 h-3.5 flex-shrink-0 text-emerald-400" />
                <div>
                  <div className="font-semibold">Normal / Active</div>
                  <div className="text-[11px] opacity-80 font-mono">
                    &lt; {criticalValue.toFixed(1)} Ω
                  </div>
                </div>
              </div>

              <div className="p-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 flex items-center gap-2">
                <FaExclamationTriangle className="w-3.5 h-3.5 flex-shrink-0 text-red-400" />
                <div>
                  <div className="font-semibold">Critical Alarm</div>
                  <div className="text-[11px] opacity-80 font-mono">
                    ≥ {criticalValue.toFixed(1)} Ω
                  </div>
                </div>
              </div>
            </div>

            <p className="text-[11px] text-slate-400 leading-relaxed pt-1">
              Sensors under <strong>{formatDisplayName(site.name)}</strong> with resistance at or above this threshold will immediately trigger critical warnings.
            </p>
          </div>

          {/* Error Message */}
          {errorMsg && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-xs flex items-center gap-2">
              <FaExclamationTriangle className="w-4 h-4 flex-shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}
        </div>

        {/* Modal Footer Actions */}
        <div
          className="flex items-center justify-end gap-2.5 sm:gap-3 px-5 sm:px-6 py-3.5 sm:py-4 relative z-10 flex-shrink-0"
          style={{
            borderTop: "1px solid rgba(255, 255, 255, 0.07)",
            background: "rgba(7, 12, 24, 0.6)",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium text-slate-400 hover:text-white hover:bg-white/[0.06] transition-all cursor-pointer disabled:opacity-40"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={isPending || !!errorMsg}
            className="px-5 sm:px-6 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-bold text-white transition-all cursor-pointer flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed shadow-lg active:scale-98"
            style={{
              background: "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)",
              boxShadow: "0 4px 16px rgba(37, 99, 235, 0.35)",
              border: "1px solid rgba(255, 255, 255, 0.15)",
            }}
          >
            {isPending ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <FaCheck className="w-3.5 h-3.5" />
                <span>Save Critical Value</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(SetCriticalValueModal);
